/* eslint-disable zillow/@typescript-eslint/naming-convention */
import abClient, { buildEndpoint } from '@zillow/ab-client';

import type { URLParams } from '../types/genericTypes';

/**
 * This object is the source of truth for all HOPS ab trials
 * [key] corresponds to the trial name as defined in `abtest` repo
 * [value] corresponds to the array of possible values for the given trial
 */
const TRIAL_DEFINITIONS = {
    SXP_NATURAL_LANGUAGE_SEARCH: ['ON', 'CONTROL'] as const,
    DXP_HOME_RECS_PHOTO_CAROUSEL: ['ON', 'CONTROL'] as const,
    HOPS_VISUAL_REFRESH: ['ON', 'CONTROL'] as const,
};

type TypedResultFromTrialDefinition<T> = T extends Record<string, readonly string[]>
    ? { [K in keyof T]?: { treatment: T[K][number] } }
    : never;
export type HopsABResult = TypedResultFromTrialDefinition<typeof TRIAL_DEFINITIONS>;

export const ABS_TIMEOUT = 1000;

/**
 * Retrieve HoPS specific AB's based on the array. If array is empty don't make the ab call
 * @param {String} guid
 * @param {Object} endpointConfig see docblock of getAbs
 * @param {boolean} mobile true if serving mobile web. false if desktop web
 * @returns {Object}
 */
export const getHomepageSpecificAbs = async (
    guid: string,
    { protocol, host, port }: URLParams,
    mobile: boolean
): Promise<HopsABResult> => {
    const trialNames = Object.keys(TRIAL_DEFINITIONS);
    if (trialNames.length === 0) {
        return {};
    }

    const forwarded_client_id = mobile ? 'mobile' : 'desktop';
    const headers = {
        client_id: 'homepage-service',
        forwarded_client_id,
    };

    const client = abClient(guid, buildEndpoint(protocol, host, port), ABS_TIMEOUT, null, headers);
    return client.getTrials(trialNames);
};

/**
 * Checks if a given treatment is set for a given trial.
 * @param {object}  obj
 * @param {object}  obj.abs         object containing ab configuration
 * @param {string}  obj.trial       the name of the trial being tested
 * @param {string}  obj.treatment   the name of the treatment
 */
export const isTreatmentForTrial = <T extends keyof typeof TRIAL_DEFINITIONS>({
    abs,
    trial,
    treatment,
}: {
    abs?: HopsABResult;
    trial: T;
    treatment: (typeof TRIAL_DEFINITIONS)[T][number];
}) => Boolean(abs?.[trial]?.treatment === treatment);
